/*
 * Author: Harper Yan
 * File Created: Thursday, 24-07-25 11:14 am
 * Last Modified: Thursday, 25-06-19 10:13 am, by Dw
 */
import type { CustomAxiosRequestConfig, SuccessResponse } from '@web/http';
import { get, post } from '@web/http';

import type {
  AuthPurpose,
  Countries,
  CountryBizType,
  ForgetPasswordParams,
  GoogleAuth,
  LoginParams,
  PerRegisterParams,
  PerRegisterRes,
  PreForgetPasswordParams,
  PreLoginParams,
  RegisterParams,
  SendGrahpCodeRes,
} from '@/@types/account';

export const getUserDetail = () => {
  return post<API.User.PostGetPersonalCenterDetailsV1Response>(
    '/gateway/user/private/v1/get-personal-center-details',
    {
      needUserInfo: true,
      needKycFillInfo: true,
      needKycCertificateInfo: true,
    },
  );
};

export const getSupportedCountries = () => {
  return get<Countries>('/gateway/country/public/v1/all-list');
};

export const getSupportedCountriesV2 = (bizType?: CountryBizType | null) => {
  return post<Countries>(
    '/gateway/country/public/v2/all-list',
    bizType ? { bizType } : {},
  );
};

export const preRegister = (params: PerRegisterParams) => {
  return post<PerRegisterRes>('/gateway/user/public/v1/pre-register', params);
};

export const register = (params: RegisterParams) => {
  const { who, purpose, ...rest } = params;

  return post('/gateway/user/public/v1/register', rest, {
    headers: {
      'x-mfa-who': who,
      'x-mfa-purpose': purpose,
    },
  });
};

export const preLogin = (params: PreLoginParams) => {
  return post('/gateway/user/public/v1/pre-login', params);
};

export const login = (params: LoginParams) => {
  const { who, purpose, ...rest } = params;

  return post('/gateway/user/public/v1/login', rest, {
    headers: {
      'x-mfa-who': who,
      'x-mfa-purpose': purpose,
    },
  });
};

export const preForgetPassword = (params: PreForgetPasswordParams) => {
  return post<{
    kycPassed: boolean;
  }>('/gateway/user/public/v1/pre-forget-password', params);
};

export const forgetPassword = (params: ForgetPasswordParams) => {
  const { who, purpose, ...rest } = params;

  return post('/gateway/user/public/v1/forget-password', rest, {
    headers: {
      'x-mfa-who': who,
      'x-mfa-purpose': purpose,
    },
  });
};

export const sendGrahpCode = async () => {
  return get<SendGrahpCodeRes>('/gateway/captcha/public/v1/send-graphs-code');
};

export const userLogout = (_data?: {
  deviceId?: string;
  terminal?: string;
}) => {
  return post<boolean>('/gateway/user/private/v1/logout', _data);
};

export const generateGoogleAuth = () => {
  return get<GoogleAuth>('/gateway/captcha/private/v1/generate-google-auth');
};

export const sendMobileCode = (
  _data: API.Captcha.PostSendMobileCodeV1Request & {
    who?: string;
    purpose?: AuthPurpose;
  },
) => {
  const { who, purpose, ...rest } = _data;

  return post('/gateway/captcha/public/v1/send-mobile-code', rest, {
    headers: {
      'x-mfa-who': who,
      'x-mfa-purpose': purpose,
    },
  });
};

export const sendEmailCode = (_data: {
  email: string;
  who?: string;
  purpose?: AuthPurpose;
}) => {
  // post: /captcha/public/v1/send-email-code
  const { who, purpose, ...rest } = _data;

  return post('/gateway/captcha/public/v1/send-email-code', rest, {
    headers: {
      'x-mfa-who': who,
      'x-mfa-purpose': purpose,
    },
  });
};

export const checkEmailExist = (email: string) => {
  return post('/gateway/captcha/private/v1/check-email-exist', { email });
};

export type AuthResponse = SuccessResponse<null | { times: number }>;

/**
 * 验证 OTP 验证码。
 *
 * @param {Object} _data - 传递给函数的数据对象。
 * @param {string} _data.who - 用户的身份标识，x-user-id。
 * @param {AuthPurpose} _data.purpose - 验证码的用途，例如登录、注册等。
 * @param {AuthType} _data.authType - 验证方式，例如短信、邮件等。
 * @param {string} _data.authCode - 用户输入的验证码。
 * @returns {Object} - 返回一个包含验证结果的对象。
 * @returns {boolean} success - 如果验证码验证通过，则为 true。
 */
export const verifyOTPCode = async (
  _data: API.Mfa.PostCaptchaAuthV1Request,
  config?: CustomAxiosRequestConfig,
) => {
  return post<null | { times: number }>(
    '/gateway/mfa/public/v1/captcha-auth',
    _data,
    config,
  );
};

export const bindGoogleAuth = (_params: {
  who: string;
  purpose: AuthPurpose;
}) => {
  // get: /private/v1/user/bind-google-auth

  return get<boolean>(
    '/gateway/user/private/v1/bind-google-auth',
    {},
    {
      headers: {
        'x-mfa-who': _params.who,
        'x-mfa-purpose': _params.purpose,
      },
    },
  );
};

export const bindEmail = (_params: { who: string; purpose: AuthPurpose }) => {
  // get: /private/v1/user/bind-email

  return get<boolean>(
    '/gateway/user/private/v1/bind-email',
    {},
    {
      headers: {
        'x-mfa-who': _params.who,
        'x-mfa-purpose': _params.purpose,
      },
    },
  );
};

export const resetPassword = (_data: {
  password: string;
  newPassword: string;
  newConfirmPassword: string;
  who: string;
  purpose: AuthPurpose;
}) => {
  // post: /private/v1/user/reset-password
  const { who, purpose, ...rest } = _data;

  return post<boolean>('/gateway/user/private/v1/reset-password', rest, {
    headers: {
      'x-mfa-who': who,
      'x-mfa-purpose': purpose,
    },
  });
};

export const resetMobile = (_data: {
  oldMobile: string;
  oldMobileCode?: string;
  newMobile: string;
  newMobileCode: string;
  who: string;
  purpose: AuthPurpose;
}) => {
  // post: /private/v1/user/reset-mobile
  const { who, purpose, ...rest } = _data;

  return post<boolean>('/gateway/user/private/v1/reset-mobile', rest, {
    headers: {
      'x-mfa-who': who,
      'x-mfa-purpose': purpose,
    },
  });
};

export const modifyLanguage = (_data: { language: string }) => {
  // post: /user/private/v1/modify-language

  return post<boolean>('/gateway/user/private/v1/modify-language', _data);
};

export const followedUser = (_data: { email: string }) => {
  // post: gateway/marketing/public/v1/followed-user

  return post<boolean>('/gateway/marketing/public/v1/followed-user', _data);
};

export const modifyUserInvitationCode = async (_params: {
  newInvitationCode: string;
}) => {
  return post<boolean>(
    '/gateway/user/private/v1/modify-invitation-code',
    _params,
  );
};

/**
 * 获取协议确认状态
 */
export const getAgreementConfirmInfo = async (
  _params: API.Tc.PostUserNewTcConfirmResultV1Request,
) => {
  return post<API.Tc.PostUserNewTcConfirmResultV1Response>(
    '/gateway/tc/private/v1/user-new-tc-confirm-result',
    _params,
  );
};

/**
 * 确认协议
 */
export const confirmAgreement = async (
  _params: API.Tc.PostUserTcConfirmV1Request,
) => {
  return post('/gateway/tc/private/v1/user-tc-confirm', _params);
};

export const modifyUserPaymentPassword = async (_data: {
  password: string;
  confirmPassword: string;
  who: string;
  purpose: AuthPurpose;
  action: 'set' | 'reset';
}) => {
  const { who, purpose, action, ...rest } = _data;
  const url =
    action === 'set'
      ? '/gateway/user/private/v1/payment-password-set'
      : '/gateway/user/private/v1/payment-password-update';

  return post<boolean>(url, rest, {
    headers: {
      'x-mfa-who': who,
      'x-mfa-purpose': purpose,
    },
  });
};

export type CaptchaType = 'GEETEST' | 'GRAPHS';

export const checkCaptchaType = async () => {
  return get<{
    captchaType: CaptchaType;
  }>('/gateway/captcha/public/v1/check-captcha-type');
};
